// Musikregeln in Worten: Chips für die Karten und die Vorlagen zum Anklicken.
//
// Ohne React, damit es sich ohne Browser prüfen lässt (musikregeln.test.ts).

import type { MusikRegelBedingungen } from "./api";

/** Sekunden als m:ss („3:30“). */
export function mmss(s: number): string {
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;
}

/** „3:30“ oder „210“ → Sekunden; leer oder unlesbar → null. */
export function sekundenAus(text: string): number | null {
  const t = text.trim();
  if (t === "") return null;
  const m = /^(\d{1,3}):([0-5]?\d)$/.exec(t);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  return /^\d+$/.test(t) ? Number(t) : null;
}

const zahl = (n: number): string => n.toLocaleString("de-DE", { maximumFractionDigits: 1 });
const gesetzt = (v: number | null | undefined): v is number => typeof v === "number" && Number.isFinite(v);
const liste = (v: string[] | undefined): string[] => (v ?? []).map((s) => s.trim()).filter(Boolean);

/** Die Bedingungen als kurze, lesbare Chips: „100–125 BPM“, „1980–1989“, „Tag: sommer“. */
export function regelChips(b: MusikRegelBedingungen): string[] {
  const chips: string[] = [];
  const tempo = gesetzt(b.bpm_min) || gesetzt(b.bpm_max);
  if (gesetzt(b.bpm_min) && gesetzt(b.bpm_max)) chips.push(b.bpm_min === b.bpm_max ? `${zahl(b.bpm_min)} BPM` : `${zahl(b.bpm_min)}–${zahl(b.bpm_max)} BPM`);
  else if (gesetzt(b.bpm_min)) chips.push(`ab ${zahl(b.bpm_min)} BPM`);
  else if (gesetzt(b.bpm_max)) chips.push(`bis ${zahl(b.bpm_max)} BPM`);
  if (tempo && gesetzt(b.bpm_sicherheit_min) && b.bpm_sicherheit_min > 0) chips.push(`Messung ≥ ${Math.round(b.bpm_sicherheit_min * 100)} % sicher`);

  if (gesetzt(b.jahr_von) && gesetzt(b.jahr_bis)) chips.push(b.jahr_von === b.jahr_bis ? String(b.jahr_von) : `${b.jahr_von}–${b.jahr_bis}`);
  else if (gesetzt(b.jahr_von)) chips.push(`ab ${b.jahr_von}`);
  else if (gesetzt(b.jahr_bis)) chips.push(`bis ${b.jahr_bis}`);

  const irgend = liste(b.tags_irgendeiner);
  if (irgend.length > 0) chips.push(`Tag: ${irgend.join(" oder ")}`);
  const alle = liste(b.tags_alle);
  if (alle.length > 0) chips.push(`${alle.length > 1 ? "Tags" : "Tag"}: ${alle.join(" + ")}`);
  const keiner = liste(b.tags_keiner);
  if (keiner.length > 0) chips.push(`ohne Tag: ${keiner.join(", ")}`);

  if (gesetzt(b.dauer_max_s)) chips.push(`unter ${mmss(b.dauer_max_s)}`);
  if (b.interpret_enthaelt?.trim()) chips.push(`Interpret: „${b.interpret_enthaelt.trim()}“`);
  if (b.interpret_nicht?.trim()) chips.push(`nicht: „${b.interpret_nicht.trim()}“`);
  const tonarten = liste(b.tonarten);
  if (tonarten.length > 0) chips.push(`Tonart: ${tonarten.join(", ")}`);
  return chips;
}

export interface RegelVorlage {
  name: string;
  farbe: string;
  bedingungen: MusikRegelBedingungen;
  /** Was die Vorlage verspricht – und was nicht. */
  hinweis: string;
}

/** Vorlagen zum Anklicken. Bewusst nur Messbares: keine „Stimmung“, kein „Genre“. */
export const REGEL_VORLAGEN: RegelVorlage[] = [
  { name: "Ruhig", farbe: "#0ea5e9", bedingungen: { bpm_max: 95 }, hinweis: "Bis 95 BPM, gemessen oder von Hand gesetzt." },
  { name: "Tanzbar", farbe: "#f97316", bedingungen: { bpm_min: 118, bpm_max: 132 }, hinweis: "118–132 BPM – das Tempo der meisten Pop- und Dance-Titel." },
  { name: "80er", farbe: "#a855f7", bedingungen: { jahr_von: 1980, jahr_bis: 1989 }, hinweis: "Jahr 1980 bis 1989." },
  { name: "90er", farbe: "#10b981", bedingungen: { jahr_von: 1990, jahr_bis: 1999 }, hinweis: "Jahr 1990 bis 1999." },
  { name: "Kurz", farbe: "#eab308", bedingungen: { dauer_max_s: 210 }, hinweis: "Kürzer als 3:30 – für knappe Stunden." },
];

/** Farben zur Auswahl – dieselbe ruhige Reihe wie bei den Uhren. */
export const REGEL_FARBEN = ["#3b82f6", "#0ea5e9", "#10b981", "#eab308", "#f97316", "#ef4444", "#ec4899", "#a855f7", "#64748b"];

/** Was im Editor steht: Texte, wie getippt. */
export interface RegelFormular {
  bpm_min: string;
  bpm_max: string;
  /** "" = egal, sonst 0..1 als Text. */
  sicherheit: string;
  jahr_von: string;
  jahr_bis: string;
  tags_irgendeiner: string;
  tags_alle: string;
  tags_keiner: string;
  /** „3:30“ oder Sekunden. */
  dauer_max: string;
  interpret_enthaelt: string;
  interpret_nicht: string;
  tonarten: string[];
}

export const FORMULAR_LEER: RegelFormular = {
  bpm_min: "", bpm_max: "", sicherheit: "", jahr_von: "", jahr_bis: "",
  tags_irgendeiner: "", tags_alle: "", tags_keiner: "", dauer_max: "", interpret_enthaelt: "", interpret_nicht: "", tonarten: [],
};

const zahlAus = (t: string): number | null => {
  const s = t.trim().replace(",", ".");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
const tagsAus = (t: string): string[] => [...new Set(t.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean))];

/** Formular → Bedingungen. Leeres fällt weg; die Sicherheit gibt es nur mit Tempo-Grenze. */
export function formularZuBedingungen(f: RegelFormular): MusikRegelBedingungen {
  const b: MusikRegelBedingungen = {};
  const bpmMin = zahlAus(f.bpm_min);
  const bpmMax = zahlAus(f.bpm_max);
  if (bpmMin !== null) b.bpm_min = bpmMin;
  if (bpmMax !== null) b.bpm_max = bpmMax;
  const sicher = zahlAus(f.sicherheit);
  if ((bpmMin !== null || bpmMax !== null) && sicher !== null && sicher > 0) b.bpm_sicherheit_min = sicher;
  const jv = zahlAus(f.jahr_von);
  const jb = zahlAus(f.jahr_bis);
  if (jv !== null) b.jahr_von = Math.round(jv);
  if (jb !== null) b.jahr_bis = Math.round(jb);
  const irgend = tagsAus(f.tags_irgendeiner);
  const alle = tagsAus(f.tags_alle);
  const keiner = tagsAus(f.tags_keiner);
  if (irgend.length) b.tags_irgendeiner = irgend;
  if (alle.length) b.tags_alle = alle;
  if (keiner.length) b.tags_keiner = keiner;
  const dauer = sekundenAus(f.dauer_max);
  if (dauer !== null && dauer > 0) b.dauer_max_s = dauer;
  if (f.interpret_enthaelt.trim()) b.interpret_enthaelt = f.interpret_enthaelt.trim();
  if (f.interpret_nicht.trim()) b.interpret_nicht = f.interpret_nicht.trim();
  if (f.tonarten.length) b.tonarten = [...f.tonarten];
  return b;
}

/** Bedingungen → Formular (zum Bearbeiten einer gespeicherten Regel). */
export function bedingungenZuFormular(b: MusikRegelBedingungen): RegelFormular {
  const t = (v: number | null | undefined): string => (typeof v === "number" ? String(v) : "");
  return {
    bpm_min: t(b.bpm_min), bpm_max: t(b.bpm_max), sicherheit: t(b.bpm_sicherheit_min),
    jahr_von: t(b.jahr_von), jahr_bis: t(b.jahr_bis),
    tags_irgendeiner: (b.tags_irgendeiner ?? []).join(", "), tags_alle: (b.tags_alle ?? []).join(", "), tags_keiner: (b.tags_keiner ?? []).join(", "),
    dauer_max: typeof b.dauer_max_s === "number" ? mmss(b.dauer_max_s) : "",
    interpret_enthaelt: b.interpret_enthaelt ?? "", interpret_nicht: b.interpret_nicht ?? "", tonarten: b.tonarten ?? [],
  };
}
